/**
 * Runtime tuning overrides — the designer's dial set.
 *
 * The catalog dictionaries (`RESOURCES`, `RECIPES`, `MACHINE_SPECS`) stay
 * as immutable *baselines*. At runtime, the server can carry a
 * `TuningStore` whose overrides win whenever a sim step reads a value
 * through one of the helpers in this module. Admin commands mutate the
 * store; the next snapshot broadcasts the whole override list to clients.
 *
 * Gatekeeping is elsewhere (the command handler checks ADMIN_TOKEN /
 * role). This module is data shape + validation + default fallback.
 */
import { ALL_RESOURCE_IDS, RESOURCES } from './resources.js';
import { MACHINE_SPECS, RECIPES, } from './machines.js';
import { DRONE_ACTIVE_DRAW_KW, DRONE_BASE_COST, DRONE_COST_GROWTH, DRONE_IDLE_DRAW_KW, STARTING_CREDITS, } from './economy.js';
import { DEFAULT_ELASTICITY, DEFAULT_PRICE_CLAMP_HI, DEFAULT_PRICE_CLAMP_LO, DEFAULT_WINDOW_TICKS, } from './market.js';
import { DEFAULT_EVENT_DURATION_JITTER, DEFAULT_EVENT_SPAWN_PER_TICK, DEFAULT_MAX_ACTIVE_EVENTS, } from './events.js';
/** Built-in baseline for drone speed (tiles/tick). One place so the
 *  TuningStore default + the addDrone fallback agree. */
export const DRONE_SPEED_BASELINE = 0.6;
// ---------- Range / label metadata ----------
function priceRange(baseline) {
    return {
        min: 0,
        max: Math.max(10_000, baseline * 50),
        step: 1,
        unit: '₡',
        defaultValue: baseline,
    };
}
function cycleRange(baseline) {
    return {
        min: 1,
        max: 10_000,
        step: 1,
        unit: 't',
        defaultValue: baseline,
    };
}
function bufferRange(baseline) {
    return {
        min: 1,
        max: 1_000_000,
        step: 10,
        unit: 'u',
        defaultValue: baseline,
    };
}
export const TUNING_RANGES = buildTuningRanges();
function buildTuningRanges() {
    const out = {};
    for (const rid of ALL_RESOURCE_IDS) {
        const baseline = RESOURCES[rid].basePrice;
        out[`resource.${rid}.basePrice`] = {
            label: `${RESOURCES[rid].name} — base price`,
            section: 'resources',
            ...priceRange(baseline),
        };
    }
    for (const id of Object.keys(RECIPES)) {
        const recipe = RECIPES[id];
        out[`recipe.${id}.cycleTicks`] = {
            label: `${recipe.name} — cycle (ticks)`,
            section: 'recipes',
            ...cycleRange(recipe.cycleTicks),
        };
        // P5+: input/output quantities are tunable so designers can rebalance a
        // chain (e.g. 2 coal → 1 fuel vs 1 coal → 1 fuel) without a rebuild.
        recipe.inputs.forEach((io, i) => {
            out[`recipe.${id}.input.${i}.qty`] = {
                label: `${recipe.name} — input ${io.resource} qty`,
                section: 'recipes',
                min: 0,
                max: 1000,
                step: 1,
                unit: 'u',
                defaultValue: io.qty,
            };
        });
        recipe.outputs.forEach((io, i) => {
            out[`recipe.${id}.output.${i}.qty`] = {
                label: `${recipe.name} — output ${io.resource} qty`,
                section: 'recipes',
                min: 0,
                max: 1000,
                step: 1,
                unit: 'u',
                defaultValue: io.qty,
            };
        });
    }
    for (const id of Object.keys(MACHINE_SPECS)) {
        const spec = MACHINE_SPECS[id];
        out[`machine.${id}.bufferCapacity`] = {
            label: `${spec.name} — buffer capacity`,
            section: 'machines',
            ...bufferRange(spec.bufferCapacity),
        };
        out[`machine.${id}.cost`] = {
            label: `${spec.name} — placement cost`,
            section: 'machines',
            min: 0,
            max: 100_000,
            step: 1,
            unit: '₡',
            defaultValue: spec.cost,
        };
        // P5: power-related fields. Only registered when the spec actually has
        // them so the admin UI doesn't show "0 → 0" knobs for inert specs.
        out[`machine.${id}.idleDrawKw`] = {
            label: `${spec.name} — idle draw`,
            section: 'machines',
            min: 0,
            max: 1000,
            step: 0.1,
            unit: 'kW',
            defaultValue: spec.idleDrawKw ?? 0,
        };
        out[`machine.${id}.activeDrawKw`] = {
            label: `${spec.name} — active draw`,
            section: 'machines',
            min: 0,
            max: 1000,
            step: 0.1,
            unit: 'kW',
            defaultValue: spec.activeDrawKw ?? 0,
        };
        out[`machine.${id}.generationKw`] = {
            label: `${spec.name} — generation`,
            section: 'machines',
            min: 0,
            max: 1000,
            step: 0.5,
            unit: 'kW',
            defaultValue: spec.generationKw ?? 0,
        };
        out[`machine.${id}.capacitorKwh`] = {
            label: `${spec.name} — capacitor capacity`,
            section: 'machines',
            min: 0,
            max: 10_000,
            step: 5,
            unit: 'kWh',
            defaultValue: spec.capacitorKwh ?? 0,
        };
    }
    out['drone.speed.baseline'] = {
        label: 'Freighter speed (tiles/tick)',
        section: 'drones',
        min: 0.05,
        max: 5,
        step: 0.05,
        unit: 't/t',
        defaultValue: DRONE_SPEED_BASELINE,
    };
    out['drone.baseCost'] = {
        label: 'Drone base cost',
        section: 'drones',
        min: 0,
        max: 100_000,
        step: 1,
        unit: '₡',
        defaultValue: DRONE_BASE_COST,
    };
    out['drone.costGrowth'] = {
        label: 'Drone cost growth factor',
        section: 'drones',
        min: 1,
        max: 5,
        step: 0.01,
        defaultValue: DRONE_COST_GROWTH,
    };
    out['drone.idleDrawKw'] = {
        label: 'Drone idle draw',
        section: 'drones',
        min: 0,
        max: 50,
        step: 0.05,
        unit: 'kW',
        defaultValue: DRONE_IDLE_DRAW_KW,
    };
    out['drone.activeDrawKw'] = {
        label: 'Drone active draw (flying/loading)',
        section: 'drones',
        min: 0,
        max: 50,
        step: 0.05,
        unit: 'kW',
        defaultValue: DRONE_ACTIVE_DRAW_KW,
    };
    out['economy.startingCredits'] = {
        label: 'Starting credits (new charter)',
        section: 'economy',
        min: 0,
        max: 1_000_000,
        step: 10,
        unit: '₡',
        defaultValue: STARTING_CREDITS,
    };
    // P4A — market dynamics knobs. Grouped under 'economy' so the admin
    // console naturally places them alongside starting credits.
    out['market.elasticity'] = {
        label: 'Market elasticity',
        section: 'economy',
        min: 0,
        max: 5,
        step: 0.05,
        defaultValue: DEFAULT_ELASTICITY,
    };
    out['market.window_ticks'] = {
        label: 'Market window (ticks)',
        section: 'economy',
        min: 10,
        max: 10_000,
        step: 10,
        unit: 't',
        defaultValue: DEFAULT_WINDOW_TICKS,
    };
    out['market.price_clamp_lo'] = {
        label: 'Price floor (× baseline)',
        section: 'economy',
        min: 0.05,
        max: 1,
        step: 0.05,
        defaultValue: DEFAULT_PRICE_CLAMP_LO,
    };
    out['market.price_clamp_hi'] = {
        label: 'Price ceiling (× baseline)',
        section: 'economy',
        min: 1,
        max: 10,
        step: 0.1,
        defaultValue: DEFAULT_PRICE_CLAMP_HI,
    };
    out['market.npc_flow_scale'] = {
        label: 'NPC flow scale',
        section: 'economy',
        min: 0,
        max: 5,
        step: 0.1,
        defaultValue: 1,
    };
    // P8 — reactive event scheduler knobs. Set spawnPerTick to 0 to disable
    // events entirely (kill switch). max=0 also disables (no spawns when full).
    out['events.spawnPerTick'] = {
        label: 'Event spawn chance per tick',
        section: 'economy',
        min: 0,
        max: 1,
        step: 0.0001,
        defaultValue: DEFAULT_EVENT_SPAWN_PER_TICK,
    };
    out['events.maxActive'] = {
        label: 'Max simultaneous events',
        section: 'economy',
        min: 0,
        max: 10,
        step: 1,
        defaultValue: DEFAULT_MAX_ACTIVE_EVENTS,
    };
    out['events.durationJitter'] = {
        label: 'Event duration jitter (±)',
        section: 'economy',
        min: 0,
        max: 1,
        step: 0.05,
        defaultValue: DEFAULT_EVENT_DURATION_JITTER,
    };
    return out;
}
export const ALL_TUNING_KEYS = Object.keys(TUNING_RANGES);
/** True iff `k` is a string that matches a known TuningKey. */
export function isTuningKey(k) {
    return k in TUNING_RANGES;
}
export function validateTuning(key, value) {
    if (!isTuningKey(key)) {
        return {
            ok: false,
            code: 'UNKNOWN_TUNING_KEY',
            message: `No tunable named "${key}".`,
        };
    }
    if (!Number.isFinite(value)) {
        return {
            ok: false,
            code: 'VALUE_NOT_FINITE',
            message: `Tuning value must be a finite number.`,
        };
    }
    const range = TUNING_RANGES[key];
    // `range` is always present — `key` is checked above against the same
    // map — but the strict-index-access flag forces a guard.
    if (!range) {
        return { ok: false, code: 'UNKNOWN_TUNING_KEY', message: `No tunable "${key}".` };
    }
    if (value < range.min || value > range.max) {
        return {
            ok: false,
            code: 'OUT_OF_RANGE',
            message: `"${key}" must be in [${range.min}, ${range.max}]; got ${value}.`,
        };
    }
    return { ok: true };
}
// ---------- Tuning-aware readers ----------
export function getResourceBasePrice(id, tuning) {
    const key = `resource.${id}.basePrice`;
    return tuning?.get(key) ?? RESOURCES[id].basePrice;
}
export function getRecipeCycleTicks(id, tuning) {
    const key = `recipe.${id}.cycleTicks`;
    return tuning?.get(key) ?? RECIPES[id].cycleTicks;
}
export function getMachineBufferCapacity(id, tuning) {
    const key = `machine.${id}.bufferCapacity`;
    return tuning?.get(key) ?? MACHINE_SPECS[id].bufferCapacity;
}
export function getMachineCost(id, tuning) {
    const key = `machine.${id}.cost`;
    return tuning?.get(key) ?? MACHINE_SPECS[id].cost;
}
// ── P5 power readers ──────────────────────────────────────────────────────
export function getMachineIdleDrawKw(id, tuning) {
    return tuning?.get(`machine.${id}.idleDrawKw`) ?? MACHINE_SPECS[id].idleDrawKw ?? 0;
}
export function getMachineActiveDrawKw(id, tuning) {
    return tuning?.get(`machine.${id}.activeDrawKw`) ?? MACHINE_SPECS[id].activeDrawKw ?? 0;
}
export function getMachineGenerationKw(id, tuning) {
    return tuning?.get(`machine.${id}.generationKw`) ?? MACHINE_SPECS[id].generationKw ?? 0;
}
export function getMachineCapacitorKwh(id, tuning) {
    return tuning?.get(`machine.${id}.capacitorKwh`) ?? MACHINE_SPECS[id].capacitorKwh ?? 0;
}
export function getDroneIdleDrawKw(tuning) {
    return tuning?.get('drone.idleDrawKw') ?? DRONE_IDLE_DRAW_KW;
}
export function getDroneActiveDrawKw(tuning) {
    return tuning?.get('drone.activeDrawKw') ?? DRONE_ACTIVE_DRAW_KW;
}
/**
 * Returns a copy of the recipe with `cycleTicks` and every input/output
 * `qty` resolved against the live tuning store. Use this in any sim step
 * that reads a recipe's numbers — falling back to `getRecipe` returns the
 * raw catalog values, ignoring overrides.
 *
 * The resource ids and the recipe's name are never tuned — only numeric
 * fields are. If you need to swap the goods themselves, that's a code
 * change to the catalog.
 */
export function getTunedRecipe(id, tuning) {
    const r = RECIPES[id];
    return {
        id: r.id,
        name: r.name,
        cycleTicks: tuning?.get(`recipe.${id}.cycleTicks`) ?? r.cycleTicks,
        inputs: r.inputs.map((io, i) => ({
            resource: io.resource,
            qty: tuning?.get(`recipe.${id}.input.${i}.qty`) ?? io.qty,
        })),
        outputs: r.outputs.map((io, i) => ({
            resource: io.resource,
            qty: tuning?.get(`recipe.${id}.output.${i}.qty`) ?? io.qty,
        })),
    };
}
export function getDroneSpeedBaseline(tuning) {
    return tuning?.get('drone.speed.baseline') ?? DRONE_SPEED_BASELINE;
}
export function getDroneBaseCostTuned(tuning) {
    return tuning?.get('drone.baseCost') ?? DRONE_BASE_COST;
}
export function getDroneCostGrowthTuned(tuning) {
    return tuning?.get('drone.costGrowth') ?? DRONE_COST_GROWTH;
}
export function getStartingCreditsTuned(tuning) {
    return tuning?.get('economy.startingCredits') ?? STARTING_CREDITS;
}
// ---------- P4A market readers ----------
export function getMarketElasticity(tuning) {
    return tuning?.get('market.elasticity') ?? DEFAULT_ELASTICITY;
}
export function getMarketWindowTicks(tuning) {
    return Math.floor(tuning?.get('market.window_ticks') ?? DEFAULT_WINDOW_TICKS);
}
export function getMarketPriceClampLo(tuning) {
    return tuning?.get('market.price_clamp_lo') ?? DEFAULT_PRICE_CLAMP_LO;
}
export function getMarketPriceClampHi(tuning) {
    return tuning?.get('market.price_clamp_hi') ?? DEFAULT_PRICE_CLAMP_HI;
}
export function getMarketNpcFlowScale(tuning) {
    return tuning?.get('market.npc_flow_scale') ?? 1;
}
//# sourceMappingURL=tuning.js.map